import React, { useState, useRef, useEffect } from 'react';

const Tooltip = ({ text, children }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const tooltipRef = useRef(null);
  const triggerRef = useRef(null);

  const showTooltip = () => {
    setIsVisible(true);
  };

  const hideTooltip = () => {
    setIsVisible(false);
  };

  const toggleTooltip = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsVisible(!isVisible);
  };

  useEffect(() => {
    if (isVisible && triggerRef.current && tooltipRef.current) {
      // Use a timeout to ensure the tooltip is rendered before calculating position
      setTimeout(() => {
        if (!tooltipRef.current || !triggerRef.current) return;
        
        const triggerRect = triggerRef.current.getBoundingClientRect();
        const tooltipRect = tooltipRef.current.getBoundingClientRect();
        
        // Position tooltip to the right of the icon
        let top = triggerRect.top + (triggerRect.height / 2) - (tooltipRect.height / 2);
        let left = triggerRect.right + 10; // 10px gap from the icon

        // If tooltip would go off the right edge of screen, show it on the left instead
        if (left + tooltipRect.width > window.innerWidth - 10) {
          left = triggerRect.left - tooltipRect.width - 10;
          // Add a class to flip the arrow
          tooltipRef.current.classList.add('tooltip-left');
        } else {
          tooltipRef.current.classList.remove('tooltip-left');
        }

        // Keep tooltip within vertical bounds
        if (top < 10) {
          top = 10;
        } else if (top + tooltipRect.height > window.innerHeight - 10) {
          top = window.innerHeight - tooltipRect.height - 10;
        }

        setPosition({ top, left });
      }, 0);
    }
  }, [isVisible]);

  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isVisible && 
          tooltipRef.current && 
          !tooltipRef.current.contains(event.target) &&
          triggerRef.current &&
          !triggerRef.current.contains(event.target)) {
        setIsVisible(false);
      }
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isVisible]);

  return (
    <span className="tooltip-wrapper">
      <span
        ref={triggerRef}
        className="tooltip-trigger"
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onClick={toggleTooltip}
      >
        {children}
        <span className="tooltip-icon">ⓘ</span>
      </span>
      {isVisible && (
        <div
          ref={tooltipRef}
          className="tooltip-content tooltip-side"
          style={{
            position: 'fixed',  // Changed from 'absolute' to 'fixed'
            top: `${position.top}px`,
            left: `${position.left}px`,
            zIndex: 9999
          }}
        >
          <div className="tooltip-arrow tooltip-arrow-side"></div>
          {text}
        </div>
      )}
    </span>
  );
};

export default Tooltip;
